import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ServicoBio, ResumoBioMetrica } from '../services/servico-bio.service';
import { AutorizacaoService } from '../../services/autorizacao.service';

declare var Chart: any;

interface InsightBio {
  titulo: string;
  descricao: string;
  prioridade: 'alta' | 'media' | 'baixa';
  icone: string;
  sugestao: string;
}

@Component({
  selector: 'app-bio-metricas',
  templateUrl: './bio-metricas.component.html',
  styleUrls: ['./bio-metricas.component.scss']
})
export class BioMetricasComponent implements OnInit {

  @ViewChild('chartVisitas', { static: false }) chartVisitasRef: ElementRef;
  @ViewChild('chartOrigens', { static: false }) chartOrigensRef: ElementRef;
  @ViewChild('chartRanking', { static: false }) chartRankingRef: ElementRef;
  @ViewChild('iframePreview', { static: false }) iframePreviewRef: ElementRef;
  @ViewChild('iframePreviewFull', { static: false }) iframePreviewFullRef: ElementRef;

  metricas: ResumoBioMetrica;
  carregando = true;
  idBioPagina: number;
  slugBioPagina: string;
  urlPreview: SafeResourceUrl;
  timestampPreview = Date.now();
  periodoSelecionado = '7d';

  periodos = [
    { valor: '1d', label: 'Hoje' },
    { valor: '7d', label: '7 dias' },
    { valor: '14d', label: '14 dias' },
    { valor: '30d', label: '30 dias' },
    { valor: '90d', label: '90 dias' }
  ];

  chartVisitas: any;
  chartOrigens: any;
  chartRanking: any;

  variacaoVisitas = 0;
  variacaoCliques = 0;
  variacaoConversao = 0;

  heatmapExpandido = false;
  mostrarTodosCliques = false;

  coresRanking = ['#4F6BED', '#4F6BED', '#4F6BED', '#4F6BED', '#4F6BED'];

  private readonly ICONES_ORIGEM: {[key: string]: string} = {
    'instagram': 'fab fa-instagram',
    'whatsapp': 'fab fa-whatsapp',
    'facebook': 'fab fa-facebook',
    'tiktok': 'fab fa-tiktok',
    'google': 'fab fa-google',
    'twitter': 'fab fa-twitter',
    'qrcode': 'fas fa-qrcode',
    'direto': 'fas fa-globe',
    'outro': 'fas fa-question'
  };

  private readonly CORES_ORIGEM: {[key: string]: string} = {
    'instagram': '#E1306C',
    'whatsapp': '#25D366',
    'facebook': '#1877F2',
    'tiktok': '#000000',
    'google': '#4285F4',
    'twitter': '#1DA1F2',
    'qrcode': '#FF6F00',
    'direto': '#607D8B',
    'outro': '#9E9E9E'
  };

  private readonly LABELS_ORIGEM: {[key: string]: string} = {
    'instagram': 'Instagram',
    'whatsapp': 'WhatsApp',
    'facebook': 'Facebook',
    'tiktok': 'TikTok',
    'google': 'Google',
    'twitter': 'Twitter/X',
    'qrcode': 'QR Code',
    'direto': 'Direto',
    'outro': 'Outro'
  };

  private readonly ICONES_DISPOSITIVO: {[key: string]: string} = {
    'iPhone': 'fab fa-apple',
    'iPad': 'fab fa-apple',
    'Android': 'fab fa-android',
    'Windows': 'fab fa-windows',
    'Mac': 'fab fa-apple',
    'Linux': 'fab fa-linux',
    'Outro': 'fas fa-question'
  };

  private readonly CORES_DISPOSITIVO: {[key: string]: string} = {
    'iPhone': '#333333',
    'iPad': '#555555',
    'Android': '#3DDC84',
    'Windows': '#0078D4',
    'Mac': '#555555',
    'Linux': '#FCC624',
    'Outro': '#9E9E9E'
  };

  constructor(
    private servicoBio: ServicoBio,
    private autorizacaoService: AutorizacaoService,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit() {
    this.autorizacaoService.fetchUser().subscribe(usuario => {
      if (!usuario?.empresa?.id) {
        this.carregando = false;
        return;
      }

      this.servicoBio.obterBioPagina(usuario.empresa.id).then((data: any) => {
        if (data && data.id) {
          this.idBioPagina = data.id;
          this.slugBioPagina = data.slug;
          this.atualizarUrlPreview();
          this.carregarMetricas();
        } else {
          this.carregando = false;
        }
      }).catch(() => {
        this.carregando = false;
      });
    });
  }

  carregarMetricas() {
    this.carregando = true;
    this.servicoBio.obterMetricas(this.idBioPagina, this.periodoSelecionado).then((data: ResumoBioMetrica) => {
      this.metricas = data;
      if (data) {
        this.variacaoVisitas = (data as any).variacaoVisitas || 0;
        this.variacaoCliques = (data as any).variacaoCliques || 0;
        this.variacaoConversao = (data as any).variacaoConversao || 0;
      }
      this.carregando = false;
      setTimeout(() => {
        this.renderizarGraficos();
        this.enviarDadosHeatmap();
      }, 100);
    }).catch(() => {
      this.carregando = false;
    });
  }

  alterarPeriodo(periodo: string) {
    this.periodoSelecionado = periodo;
    this.carregarMetricas();
  }

  // ============ Preview / Heatmap ============

  atualizarUrlPreview() {
    if (!this.slugBioPagina) {
      this.urlPreview = null;
      return;
    }
    const url = window.location.origin + '/bio/' + this.slugBioPagina + '?preview=true&heatmap=true&t=' + this.timestampPreview;
    this.urlPreview = this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  enviarDadosHeatmap() {
    this.enviarParaIframe(this.iframePreviewRef);
    this.enviarParaIframe(this.iframePreviewFullRef);
  }

  private enviarParaIframe(ref: ElementRef) {
    if (!ref || !this.metricas?.cliquesPorItem?.length) return;
    const iframe = ref.nativeElement as HTMLIFrameElement;
    if (!iframe.contentWindow) return;
    const maxQuantidade = Math.max(...this.metricas.cliquesPorItem.map(i => i.quantidade), 1);
    iframe.contentWindow.postMessage({
      tipo: 'bio-heatmap-data',
      cliques: this.metricas.cliquesPorItem.map(item => ({
        tipoItem: item.tipoItem,
        idItem: item.idItem,
        quantidade: item.quantidade,
        percentual: item.percentual
      })),
      maxQuantidade: maxQuantidade
    }, '*');
  }

  onIframeLoad() {
    setTimeout(() => this.enviarParaIframe(this.iframePreviewRef), 500);
  }

  onIframeLoadFull() {
    setTimeout(() => this.enviarParaIframe(this.iframePreviewFullRef), 500);
  }

  recarregarPreview() {
    this.timestampPreview = Date.now();
    this.atualizarUrlPreview();
  }

  expandirHeatmap() {
    this.heatmapExpandido = true;
    setTimeout(() => {
      this.enviarParaIframe(this.iframePreviewFullRef);
    }, 600);
  }

  fecharHeatmap() {
    this.heatmapExpandido = false;
    setTimeout(() => {
      this.renderizarGraficos();
      this.enviarParaIframe(this.iframePreviewRef);
    }, 100);
  }

  // ============ Computed ============

  get cliquesOfertas(): number {
    if (!this.metricas?.cliquesPorItem?.length) return 0;
    return this.metricas.cliquesPorItem
      .filter(i => i.tipoItem === 'oferta')
      .reduce((acc, i) => acc + i.quantidade, 0);
  }

  get cliquesCardapio(): number {
    if (!this.metricas?.cliquesPorItem?.length) return 0;
    return this.metricas.cliquesPorItem
      .filter(i => i.tipoItem === 'oferta' || i.titulo.toLowerCase().includes('cardapio') || i.titulo.toLowerCase().includes('cardápio'))
      .reduce((acc, i) => acc + i.quantidade, 0);
  }

  get cliquesIfood(): number {
    if (!this.metricas?.cliquesPorItem?.length) return 0;
    return this.metricas.cliquesPorItem
      .filter(i => i.titulo.toLowerCase().includes('ifood'))
      .reduce((acc, i) => acc + i.quantidade, 0);
  }

  get ctrOfertas(): number {
    if (!this.metricas?.totalVisitas) return 0;
    return Math.round((this.cliquesOfertas / this.metricas.totalVisitas) * 1000) / 10;
  }

  get participacaoOfertas(): number {
    if (!this.metricas?.totalCliques) return 0;
    return Math.round((this.cliquesOfertas / this.metricas.totalCliques) * 1000) / 10;
  }

  get mediaCliques(): number {
    if (!this.metricas?.totalVisitas) return 0;
    return this.metricas.totalCliques / this.metricas.totalVisitas;
  }

  get percentualMobile(): number {
    if (!this.metricas?.dispositivos?.length) return 0;
    const mobile = this.metricas.dispositivos
      .filter(d => ['iPhone', 'Android', 'iPad'].includes(d.dispositivo))
      .reduce((acc, d) => acc + d.percentual, 0);
    return mobile;
  }

  get insightsAutomaticos(): InsightBio[] {
    if (!this.metricas) return [];

    const insights: InsightBio[] = [];
    const totalVisitas = this.metricas.totalVisitas || 0;
    const ctrGeral = this.metricas.taxaConversao || 0;
    const totalCliques = this.metricas.totalCliques || 0;

    if (totalVisitas >= 20 && totalCliques === 0) {
      insights.push({
        titulo: 'Sua bio está recebendo visitas, mas sem cliques',
        descricao: `${totalVisitas} visitas no período e nenhum clique registrado.`,
        prioridade: 'alta',
        icone: 'fas fa-exclamation-triangle',
        sugestao: 'Revise os títulos dos primeiros links e deixe o CTA principal mais claro, com benefício direto.'
      });
    }

    if (totalVisitas >= 20 && ctrGeral > 0 && ctrGeral < 20) {
      insights.push({
        titulo: 'CTR geral abaixo do ideal',
        descricao: `Taxa atual de ${ctrGeral.toFixed(1)}%.`,
        prioridade: 'alta',
        icone: 'fas fa-chart-line',
        sugestao: 'Suba o link mais importante para o topo e use subtítulos curtos orientados à ação.'
      });
    }

    const melhorOrigem = [...(this.metricas.origens || [])]
      .filter(o => o.visitas >= 5)
      .sort((a, b) => b.ctr - a.ctr)[0];

    if (melhorOrigem && melhorOrigem.ctr > (ctrGeral * 1.2)) {
      insights.push({
        titulo: `${this.obtenhaLabelOrigem(melhorOrigem.origem)} converte melhor`,
        descricao: `CTR de ${melhorOrigem.ctr.toFixed(1)}% nessa origem.`,
        prioridade: 'media',
        icone: this.obtenhaIconeOrigem(melhorOrigem.origem),
        sugestao: 'Aplique UTMs nessa campanha e direcione esse público para o seu link mais lucrativo.'
      });
    }

    const piorCtr = [...(this.metricas.ctrPorLink || [])]
      .filter(c => c.tipoItem !== 'horarios' && c.cliques >= 2)
      .sort((a, b) => a.ctr - b.ctr)[0];

    if (piorCtr && totalVisitas >= 20 && piorCtr.ctr < 8) {
      insights.push({
        titulo: `Item com baixa conversão: ${piorCtr.titulo}`,
        descricao: `CTR de ${piorCtr.ctr.toFixed(1)}% com ${piorCtr.cliques} cliques.`,
        prioridade: 'media',
        icone: 'fas fa-link',
        sugestao: 'Teste novo título/subtítulo e reposicione esse item para validar se o problema é copy ou posição.'
      });
    }

    if (this.percentualMobile >= 70) {
      insights.push({
        titulo: 'Tráfego majoritariamente mobile',
        descricao: `${this.percentualMobile.toFixed(1)}% das visitas vêm de celular.`,
        prioridade: 'baixa',
        icone: 'fas fa-mobile-alt',
        sugestao: 'Priorize links curtos no topo e use thumbs/imagens com contraste alto para leitura rápida.'
      });
    }

    if (this.participacaoOfertas >= 30 && this.cliquesOfertas >= 10) {
      insights.push({
        titulo: 'Ofertas estão puxando os cliques',
        descricao: `${this.participacaoOfertas.toFixed(1)}% dos cliques vieram de ofertas.`,
        prioridade: 'baixa',
        icone: 'fas fa-bullhorn',
        sugestao: 'Mantenha as ofertas atualizadas e teste novos badges para aumentar ainda mais o desempenho.'
      });
    }

    if (insights.length === 0) {
      insights.push({
        titulo: 'Sem alertas críticos no período',
        descricao: 'A performance está estável com os dados atuais.',
        prioridade: 'baixa',
        icone: 'fas fa-check-circle',
        sugestao: 'Continue monitorando semanalmente e rode pequenos testes de título/ordem dos links.'
      });
    }

    return insights.slice(0, 5);
  }

  // ============ Helpers ============

  obtenhaIconeDispositivo(dispositivo: string): string {
    return this.ICONES_DISPOSITIVO[dispositivo] || 'fas fa-question';
  }

  obtenhaCorDispositivo(dispositivo: string): string {
    return this.CORES_DISPOSITIVO[dispositivo] || '#9E9E9E';
  }

  obtenhaIconeOrigem(origem: string): string {
    return this.ICONES_ORIGEM[origem] || 'fas fa-question';
  }

  obtenhaCorOrigem(origem: string): string {
    return this.CORES_ORIGEM[origem] || '#9E9E9E';
  }

  obtenhaLabelOrigem(origem: string): string {
    return this.LABELS_ORIGEM[origem] || origem;
  }

  get maxCtrPorLink(): number {
    if (!this.metricas?.ctrPorLink?.length) return 1;
    return Math.max(...this.metricas.ctrPorLink.map(c => c.ctr), 1);
  }

  // ============ Charts ============

  private renderizarGraficos() {
    this.renderizarGraficoVisitas();
    this.renderizarGraficoOrigens();
    this.renderizarGraficoRanking();
  }

  private renderizarGraficoVisitas() {
    if (!this.chartVisitasRef || !this.metricas?.visitasPorDia?.length) return;

    if (this.chartVisitas) {
      this.chartVisitas.destroy();
    }

    const ctx = this.chartVisitasRef.nativeElement.getContext('2d');
    const labels = this.metricas.visitasPorDia.map(d => {
      const parts = d.data.split('-');
      return parts.length >= 3 ? parts[2] + '/' + parts[1] : d.data;
    });
    const dadosVisitas = this.metricas.visitasPorDia.map(d => d.quantidade);

    const mapaCliques: {[key: string]: number} = {};
    (this.metricas.cliquesPorDia || []).forEach(c => { mapaCliques[c.data] = c.quantidade; });
    const dadosCliques = this.metricas.visitasPorDia.map(d => mapaCliques[d.data] || 0);

    const usarBarras = labels.length <= 3;

    this.chartVisitas = new Chart(ctx, {
      type: usarBarras ? 'bar' : 'line',
      data: {
        labels: labels,
        datasets: [{
          label: 'Visitas',
          data: dadosVisitas,
          borderColor: '#4F6BED',
          backgroundColor: usarBarras ? 'rgba(79, 107, 237, 0.5)' : 'rgba(79, 107, 237, 0.08)',
          ...(usarBarras ? {} : { fill: true, tension: 0.3 }),
          pointBackgroundColor: '#4F6BED',
          pointRadius: 4,
          pointHoverRadius: 6
        }, {
          label: 'Cliques',
          data: dadosCliques,
          borderColor: '#10b981',
          backgroundColor: usarBarras ? 'rgba(16, 185, 129, 0.5)' : 'rgba(16, 185, 129, 0.08)',
          ...(usarBarras ? {} : { fill: true, tension: 0.3 }),
          pointBackgroundColor: '#10b981',
          pointRadius: 4,
          pointHoverRadius: 6
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: true },
        scales: {
          yAxes: [{
            ticks: { beginAtZero: true }
          }]
        }
      }
    });
  }

  private renderizarGraficoRanking() {
    if (!this.chartRankingRef || !this.metricas?.cliquesPorItem?.length) return;

    if (this.chartRanking) {
      this.chartRanking.destroy();
    }

    const ctx = this.chartRankingRef.nativeElement.getContext('2d');

    // Agrupar ofertas em uma única entrada
    let totalOfertas = 0;
    const itensAgrupados: {titulo: string, quantidade: number, tipoItem: string}[] = [];
    for (const item of this.metricas.cliquesPorItem) {
      if (item.tipoItem === 'oferta') {
        totalOfertas += item.quantidade;
      } else {
        itensAgrupados.push(item);
      }
    }
    if (totalOfertas > 0) {
      itensAgrupados.push({ titulo: 'Ofertas', quantidade: totalOfertas, tipoItem: 'oferta' });
    }
    itensAgrupados.sort((a, b) => b.quantidade - a.quantidade);

    const labels = itensAgrupados.map(i => i.titulo);
    const dados = itensAgrupados.map(i => i.quantidade);
    const cores = itensAgrupados.map(i => {
      if (i.tipoItem === 'oferta') return '#E65100';
      if (i.tipoItem === 'horarios') return '#827717';
      return '#4F6BED';
    });

    this.chartRanking = new Chart(ctx, {
      type: 'horizontalBar',
      data: {
        labels: labels,
        datasets: [{
          data: dados,
          backgroundColor: cores,
          borderWidth: 0
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          xAxes: [{
            ticks: { beginAtZero: true, stepSize: 1 }
          }],
          yAxes: [{
            barThickness: 20,
            ticks: { fontSize: 13 }
          }]
        }
      }
    });
  }

  private renderizarGraficoOrigens() {
    if (!this.chartOrigensRef || !this.metricas?.origens?.length) return;

    if (this.chartOrigens) {
      this.chartOrigens.destroy();
    }

    const ctx = this.chartOrigensRef.nativeElement.getContext('2d');
    const labels = this.metricas.origens.map(o => this.obtenhaLabelOrigem(o.origem));
    const dados = this.metricas.origens.map(o => o.visitas);
    const cores = this.metricas.origens.map(o => this.obtenhaCorOrigem(o.origem));

    this.chartOrigens = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: labels,
        datasets: [{
          data: dados,
          backgroundColor: cores,
          borderWidth: 2,
          borderColor: '#fff'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: 'bottom',
          labels: { fontSize: 11, padding: 10 }
        },
        cutoutPercentage: 60
      }
    });
  }
}
